import { Config } from '@chegg-wt/core/config'
import { AppEnvs } from '@chegg-wt/core/constants/global'
import { authRoutes } from '@chegg-wt/core/constants/user'
import { AppState, LogicDeps } from '@chegg-wt/core/state'
import { FSACreatorPayload } from '@chegg-wt/core/state/types'
import { User } from '@chegg-wt/core/types/auth'
import { Cookies, getCookie, setCookie } from '@chegg-wt/core/utils/cookies'
import $ from 'jquery'
import Router from 'next/router'
import { Action, createLogic } from 'redux-logic'
import { layoutNameFromWidth } from '@chegg-wt/core/utils/adframe'
import { reloadAds, refreshAds } from '@chegg-wt/core/utils/ads'
import {
  getLocalBibInfo,
  getUserBibInfo,
  setLocal,
} from '@chegg-wt/core/utils/localStorage'
import {
  addQueryStringToRoute,
  isWebRoute,
  routeToHref,
} from '@chegg-wt/core/utils/routes'
import { OpenModalPayload } from '../modals/types'
import { COPY_SUCCESS_MODAL } from '../modals/constants'
import { openModal } from '../modals/actions'
import { isProSelector } from '../user/selectors'
import { sbmLoadedSelector, adzoneSBMSelector, routeParamsSelector } from './selectors'
import { setAdzone, setSbmLoaded, navigated, flowed } from './actions'
import { ActionTypes } from './constants'

interface SiteData {
  currentUser: User
  citationCount: number
  pageCount: number
}

const retrieveLocalStorageValues = (): SiteData => {
  const localBibInfo = getLocalBibInfo()
  return {
    currentUser: getUserBibInfo(),
    citationCount: localBibInfo.citationCount || 0,
    pageCount: (localBibInfo.pageCount || 0) + 1,
  }
}

const setPageCount = (siteData: SiteData) => {
  setLocal('pageCount', siteData.pageCount)
}

const getVersionObj = () => {
  const { force_ad_version: forceAdVersion } = Router.query
  const cellName = getCookie(Cookies.AdLayout)

  return {
    name: (forceAdVersion as string) || cellName,
  }
}

const setVersionCookie = (domain, version) => {
  const cookie = Cookies.AdLayout

  const now = new Date()
  const tenYearsFromNow = new Date(now.setFullYear(now.getFullYear() + 10))
  if (getCookie(cookie) !== version) {
    // archie@edge will handle setting cookies for the application in non-development environments.
    if (Config.appEnv === AppEnvs.DEVELOPMENT) {
      setCookie(cookie, version, { domain, expires: tenYearsFromNow })
    }
  }
}

const initializeSBM = ({ domain }) => {
  const siteData = retrieveLocalStorageValues()
  setPageCount(siteData)
  const versionObj = getVersionObj()
  const version = versionObj.name

  setVersionCookie(domain, version)
}

export const initializeSBMLogic = createLogic<AppState, Action, any, LogicDeps>({
  type: ActionTypes.SBM_INIT,
  processOptions: {
    dispatchReturn: true,
  },
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const isPro = isProSelector(state)
    const sbmLoaded = sbmLoadedSelector(state)
    const isAuthPage = authRoutes.includes(Router.pathname)
    const isLogoutPage = Router.pathname === '/logout'

    // do not want to initialize sbm if paid member or if sbm is already present
    if (isPro || sbmLoaded || (isAuthPage && !isLogoutPage)) {
      reject(undefined)
    } else {
      allow(action)
    }
  },
  process({ siteEnv }) {
    // dependency for sbm ads-modal
    window.jQuery = $

    initializeSBM(siteEnv)
    return setSbmLoaded(true)
  },
})

export const adzoneLogic = createLogic<
  AppState,
  ReturnType<typeof setAdzone>['payload'],
  any,
  LogicDeps
>({
  type: ActionTypes.SET_ADZONE,
  process({ getState }) {
    const state = getState()
    const adzone = adzoneSBMSelector(state)
    const layout = layoutNameFromWidth(window.innerWidth)
    let { pageCount } = getLocalBibInfo()
    window.hardPageload = false
    pageCount += 1

    reloadAds(adzone, 'pagechange', layout)
    setLocal('pageCount', pageCount)
  },
})

export const refreshAdsOnCitationCopyLogic = createLogic<
  AppState,
  OpenModalPayload,
  any,
  LogicDeps
>({
  type: [openModal],
  validate({ action }, allow, reject) {
    const { name } = action.payload
    if (name === COPY_SUCCESS_MODAL) {
      return allow(action)
    }

    return reject(action)
  },
  process() {
    refreshAds()
  },
})

export const flowLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof flowed>,
  any,
  LogicDeps
>({
  type: [flowed],
  transform({ action, getState, routes }, next) {
    const { nextFlow } = action.payload
    const routeParams = routeParamsSelector(getState())
    next(navigated(routes[nextFlow](routeParams)))
  },
})

export const redirectLogic = createLogic<AppState, FSACreatorPayload<typeof navigated>>({
  type: [navigated],
  process({ action }) {
    const { route: routeWithoutQueryString, opts } = action.payload
    const route = addQueryStringToRoute(routeWithoutQueryString, opts.query || {})
    if (isWebRoute(route)) {
      window.location.href = routeToHref(route)
    } else if (opts.replace) {
      Router.replace(route.href, route.as).catch(
        (error) => error /* Do something here at some point */
      )
    } else {
      Router.push(route.href, route.as).catch(
        (error) => error /* Do something here at some point */
      )
    }
  },
})
